import React from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { bindActionCreators } from "redux"
import { Status, Table, ButtonDropdown } from "@bitcine/cs-theme"
import Select from "@src/components/select/"
import { changeUserRole, toggleUserStatus, resendUserInvite, deleteUser } from "@api/settings/team"
import { can, MODIFY_USERS_AND_ROLES, DELETE_USERS_AND_ROLES } from "@src/helpers/authorization"

const BACKGROUNDS = {
  Active: "#00d19c",
  Pending: "#ffc765",
  Suspended: "#ff5a5a"
}

const userActions = (user, member, { toggleUserStatus, resendUserInvite, deleteUser }) => {
  const canModify = can(user, [MODIFY_USERS_AND_ROLES])
  const isOwner = user._id === user.organization.owner_id
  return [
    {
      text: "Suspend",
      show: canModify && member.status === "Active",
      disabled: member.is_owner,
      onClick: () => toggleUserStatus(member),
      icon: "lock"
    },
    {
      text: "Activate",
      show: canModify && member.status === "Suspended" && !member.is_owner,
      onClick: () => toggleUserStatus(member),
      icon: "lock_open"
    },
    {
      text: "Resend Invite",
      show: canModify && member.status === "Pending",
      onClick: () => resendUserInvite(member),
      icon: "email"
    },
    {
      text: "Remove",
      // Show if member is not owner, and current user is an admin or owner with permission to delete users
      show: !member.is_owner && (user.is_org_admin || isOwner) && can(user, [DELETE_USERS_AND_ROLES]),
      // Disable if member is an admin and user is not the owner
      disabled: (member.is_admin && !isOwner),
      onClick: () => deleteUser(member),
      icon: "delete"
    }
  ].filter(({ show }) => show)
}

const List = ({ team, roles, user, changeUserRole, toggleUserStatus, resendUserInvite, deleteUser }) => (
  <Status pending={team.status === "PENDING"} error={team.status === "ERROR"}>
    <Table
      widths={["auto", 260, 200, 65]}
      className='mt2'
      header={{
        columns: [{ text: "User" }, { text: "Role" }, { text: "Status" }, { text: "" }]
      }}
      body={{
        data: team.list.filter(({ full_name = "" }) =>
          full_name
            .toLowerCase()
            .trim()
            .includes(team.search.toLowerCase().trim())
        ),
        row: {
          render: [
            data => (
              <div>
                {data.full_name}
                <small className='block muted'>{data.email}</small>
              </div>
            ),
            data =>
              data.is_owner ? (
                data.role_name + ' (Owner)'
              ) : !can(user, [MODIFY_USERS_AND_ROLES]) ? (
                data.role_name
              ) : (
                <Select
                  options={roles}
                  value={roles.find(({ _id }) => _id === data.role_id)}
                  clearable={false}
                  className='col-12 mr2'
                  onChange={role => changeUserRole(data._id, role.value)}/>
              ),
            data => (
              <div className='flex items-center'>
                <div
                  style={{
                    background: BACKGROUNDS[data.status],
                    width: '8px',
                    height: '8px',
                  }}
                  className='inline-block mr1 circle'/>
                {data.status}
              </div>
            ),
            data => {
              const actions = userActions(user, data, { toggleUserStatus, resendUserInvite, deleteUser })
              return actions.length > 0 &&
                <ButtonDropdown
                  button={{
                    className: "material-icons",
                    text: "more_vert",
                    menu: true
                  }}
                  dropdown={{
                    clickCloses: true,
                    content: actions
                  }}/>
            }
          ]
        }
      }}/>
  </Status>
)

List.propTypes = {
  team: PropTypes.shape({
    status: PropTypes.string.isRequired,
    list: PropTypes.array.isRequired,
    search: PropTypes.string.isRequired
  }).isRequired,
  roles: PropTypes.array.isRequired,
  changeUserRole: PropTypes.func.isRequired,
  toggleUserStatus: PropTypes.func.isRequired,
  resendUserInvite: PropTypes.func.isRequired,
  deleteUser: PropTypes.func.isRequired
}

const mapStateToProps = state => ({
  team: state.settings.team,
  roles: state.settings.roles.list.map(role => ({ _id: role._id, value: role._id, label: role.name })),
  user: state.user
})

const mapDispatchToProps = dispatch => ({
  changeUserRole: bindActionCreators(changeUserRole, dispatch),
  toggleUserStatus: bindActionCreators(toggleUserStatus, dispatch),
  resendUserInvite: bindActionCreators(resendUserInvite, dispatch),
  deleteUser: bindActionCreators(deleteUser, dispatch)
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(List)
